import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { getApiBase } from "../apiBase";
import { isCvinExpansion, isMultisiteParentRow } from "../projectGridColumns";
import { readSubsegmentSummaryFromAcf } from "./expansionProjectAcf";
import { buildExpansionSegmentGroups } from "./expansionGroupUtils";
import { getViewerEmailFromStorage } from "./userMatrixStorage";
import { canAccessInspectorAddReport } from "./expansionInspectorAccess";
import { formatInspectorLabel } from "./inspectorReportCalendarUtils";
import ExpansionDailyProgressForm from "./ExpansionDailyProgressForm";
import { submitDailyProgressReport } from "./expansionDailyProgressApi";
import { contractsPluginEnabled } from "./contractsAccess";
import { fetchContractorOptions, fetchProjectContractFields } from "./contractsApi";
import {
  getProjectContractorOptions,
  mergeContractorOptionLists,
} from "./inspectorReportContractors";
import "./inspectorAddReportPage.css";
import "./expansionFootageProgressBar.css";

function normalizeWorkOrder(value) {
  return String(value ?? "").trim().toLowerCase();
}

function commercialRowsFromProjects(rows) {
  return (Array.isArray(rows) ? rows : []).filter(
    (r) => r && !isCvinExpansion(r) && !isMultisiteParentRow(r)
  );
}

export default function InspectorAddReportPage({ apiUrl, getAuthHeaders }) {
  const viewerEmail = getViewerEmailFromStorage();
  const allowed = canAccessInspectorAddReport(viewerEmail);
  const inspectorLabel = formatInspectorLabel(viewerEmail);

  const [loadingProjects, setLoadingProjects] = useState(true);
  const [expansionProjects, setExpansionProjects] = useState([]);
  const [commercialProjects, setCommercialProjects] = useState([]);
  const [isCommercial, setIsCommercial] = useState(true);
  const [segmentKey, setSegmentKey] = useState("");
  const [expansionProjectId, setExpansionProjectId] = useState("");
  const [commercialWorkOrder, setCommercialWorkOrder] = useState("");
  const [contractor, setContractor] = useState("");
  const [globalContractorOptions, setGlobalContractorOptions] = useState([]);
  const [projectContractFields, setProjectContractFields] = useState(null);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [conduitFootage, setConduitFootage] = useState("");
  const [fiberFootage, setFiberFootage] = useState("");
  const [proofingFootage, setProofingFootage] = useState("");
  const [vaults, setVaults] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    setLoadingProjects(true);
    (async () => {
      try {
        const base = apiUrl || getApiBase();
        const { data } = await axios.get(`${base}/projects`, { headers: getAuthHeaders() });
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : [];
        const expansion = rows.filter(isCvinExpansion);
        const commercial = commercialRowsFromProjects(rows);
        setExpansionProjects(expansion);
        setCommercialProjects(commercial);
        const groups = buildExpansionSegmentGroups(expansion);
        if (groups.length) {
          setSegmentKey(groups[0].key);
          setExpansionProjectId(String(groups[0].children[0]?.id ?? ""));
        }
        if (commercial.length) {
          setCommercialWorkOrder(String(commercial[0].work_order ?? ""));
        }
      } catch {
        if (!cancelled) {
          setExpansionProjects([]);
          setCommercialProjects([]);
        }
      } finally {
        if (!cancelled) setLoadingProjects(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, apiUrl, getAuthHeaders]);

  useEffect(() => {
    if (!allowed || !contractsPluginEnabled()) return;
    let cancelled = false;
    (async () => {
      try {
        const options = await fetchContractorOptions({ apiUrl, getAuthHeaders });
        if (!cancelled) setGlobalContractorOptions(options);
      } catch {
        if (!cancelled) setGlobalContractorOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed, apiUrl, getAuthHeaders]);

  const segmentGroups = useMemo(
    () => buildExpansionSegmentGroups(expansionProjects),
    [expansionProjects]
  );

  const selectedGroup = useMemo(
    () => segmentGroups.find((g) => g.key === segmentKey) || null,
    [segmentGroups, segmentKey]
  );

  const subsegmentOptions = selectedGroup?.children ?? [];

  useEffect(() => {
    if (isCommercial) return;
    if (!selectedGroup?.children?.length) {
      setExpansionProjectId("");
      return;
    }
    if (!selectedGroup.children.some((c) => String(c.id) === String(expansionProjectId))) {
      setExpansionProjectId(String(selectedGroup.children[0].id));
    }
  }, [expansionProjectId, isCommercial, selectedGroup]);

  const selectedExpansionRow = useMemo(
    () => expansionProjects.find((p) => String(p.id) === String(expansionProjectId)) || null,
    [expansionProjectId, expansionProjects]
  );

  const selectedCommercialRow = useMemo(() => {
    const wo = normalizeWorkOrder(commercialWorkOrder);
    if (!wo) return null;
    return (
      commercialProjects.find((p) => normalizeWorkOrder(p.work_order) === wo) ||
      commercialProjects.find((p) => normalizeWorkOrder(p.work_order).includes(wo)) ||
      null
    );
  }, [commercialProjects, commercialWorkOrder]);

  const selectedProjectRow = isCommercial ? selectedCommercialRow : selectedExpansionRow;

  useEffect(() => {
    if (!selectedProjectRow?.id) {
      setProjectContractFields(null);
      setLoadingContractors(false);
      return;
    }
    if (!contractsPluginEnabled()) {
      setProjectContractFields(null);
      setLoadingContractors(false);
      return;
    }
    let cancelled = false;
    setLoadingContractors(true);
    (async () => {
      try {
        const data = await fetchProjectContractFields(selectedProjectRow.id, {
          apiUrl,
          getAuthHeaders,
        });
        if (!cancelled) setProjectContractFields(data?.fields ?? null);
      } catch {
        if (!cancelled) setProjectContractFields(null);
      } finally {
        if (!cancelled) setLoadingContractors(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, getAuthHeaders, selectedProjectRow?.id]);

  const projectContractorOptions = useMemo(
    () => getProjectContractorOptions(selectedProjectRow, projectContractFields),
    [projectContractFields, selectedProjectRow]
  );

  const contractorOptions = useMemo(
    () => mergeContractorOptionLists(projectContractorOptions, globalContractorOptions),
    [globalContractorOptions, projectContractorOptions]
  );

  useEffect(() => {
    if (!selectedProjectRow) {
      setContractor("");
      return;
    }
    setContractor((current) => {
      const trimmed = String(current || "").trim();
      if (!trimmed) return "";
      return contractorOptions.some((name) => name.toLowerCase() === trimmed.toLowerCase())
        ? trimmed
        : "";
    });
  }, [contractorOptions, selectedProjectRow?.id]);

  const resetFormFields = useCallback(() => {
    setConduitFootage("");
    setFiberFootage("");
    setProofingFootage("");
    setVaults("");
    setNotes("");
    setError("");
  }, []);

  const submit = useCallback(async () => {
    setSuccess("");
    setError("");
    const projectRow = isCommercial ? selectedCommercialRow : selectedExpansionRow;
    if (!projectRow) {
      setError(isCommercial ? "Select a valid commercial work order." : "Select a subsegment.");
      return;
    }
    setBusy(true);
    try {
      await submitDailyProgressReport({
        projectRow,
        isCommercial,
        report: {
          conduitFootage,
          fiberFootage,
          proofingFootage,
          vaults,
          notes,
          contractor: String(contractor || "").trim() || undefined,
        },
        apiUrl,
        getAuthHeaders,
      });
      resetFormFields();
      const wo = projectRow.work_order ?? projectRow.id;
      setSuccess(`Report saved for work order ${wo}.`);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || "Could not save report.");
    } finally {
      setBusy(false);
    }
  }, [
    apiUrl,
    conduitFootage,
    contractor,
    fiberFootage,
    getAuthHeaders,
    isCommercial,
    notes,
    proofingFootage,
    resetFormFields,
    selectedCommercialRow,
    selectedExpansionRow,
    vaults,
  ]);

  if (!allowed) {
    return (
      <div className="inspector-add-report-page">
        <div className="inspector-add-report-page-inner">
          <p className="project-detail-muted">Add Report is limited to Inspectors, Construction Leads, and OSP Engineers.</p>
          <Link to="/" className="project-detail-back">
            ← Back to board
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="inspector-add-report-page">
      <div className="inspector-add-report-page-inner">
        <Link to="/" className="project-detail-back">
          ← Back to board
        </Link>
        <h1 className="inspector-add-report-title">Add Report</h1>
        <p className="inspector-add-report-lead">
          Submit daily conduit, fiber, proofing, and handhole progress for expansion subsegments or
          commercial work orders.
        </p>

        {loadingProjects ? <p className="project-detail-muted">Loading projects…</p> : null}

        <form
          className="inspector-add-report-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <section className="inspector-add-report-section" aria-labelledby="inspector-add-report-project-heading">
            <h2 id="inspector-add-report-project-heading" className="inspector-add-report-section-title">
              Project
            </h2>
            <p className="inspector-add-report-inspector-line">
              Reporter: <strong>{inspectorLabel}</strong>
            </p>

            <label className="inspector-add-report-checkbox">
              <input
                type="checkbox"
                checked={isCommercial}
                disabled={busy || loadingProjects}
                onChange={(e) => {
                  setIsCommercial(e.target.checked);
                  setError("");
                  setSuccess("");
                }}
              />
              <span>Commercial project (not expansion)</span>
            </label>

            {!isCommercial ? (
              <>
                <label className="phase-schedule-field">
                  <span className="phase-schedule-field-label">Segment</span>
                  <select
                    className="commercial-field-input"
                    value={segmentKey}
                    disabled={busy || loadingProjects || !segmentGroups.length}
                    onChange={(e) => setSegmentKey(e.target.value)}
                  >
                    {segmentGroups.length === 0 ? (
                      <option value="">No expansion segments</option>
                    ) : (
                      segmentGroups.map((g) => (
                        <option key={g.key} value={g.key}>
                          {g.title}
                        </option>
                      ))
                    )}
                  </select>
                </label>

                <label className="phase-schedule-field">
                  <span className="phase-schedule-field-label">Work order (subsegment)</span>
                  <select
                    className="commercial-field-input"
                    value={expansionProjectId}
                    disabled={busy || !subsegmentOptions.length}
                    onChange={(e) => setExpansionProjectId(e.target.value)}
                  >
                    {subsegmentOptions.map((row) => {
                      const sub = readSubsegmentSummaryFromAcf(row.acf_groups_json);
                      const label = [row.work_order, sub?.project_name ?? row.name]
                        .filter(Boolean)
                        .join(" · ");
                      return (
                        <option key={row.id} value={row.id}>
                          {label || row.id}
                        </option>
                      );
                    })}
                  </select>
                </label>
              </>
            ) : (
              <label className="phase-schedule-field">
                <span className="phase-schedule-field-label">Commercial work order</span>
                <input
                  className="commercial-field-input"
                  type="text"
                  list="inspector-commercial-work-orders"
                  value={commercialWorkOrder}
                  disabled={busy || loadingProjects}
                  placeholder="e.g. 14120 or 14120.01"
                  onChange={(e) => setCommercialWorkOrder(e.target.value)}
                />
                <datalist id="inspector-commercial-work-orders">
                  {commercialProjects.map((row) => (
                    <option
                      key={row.id}
                      value={String(row.work_order ?? "")}
                      label={[row.work_order, row.name].filter(Boolean).join(" · ")}
                    />
                  ))}
                </datalist>
                {commercialWorkOrder && !selectedCommercialRow ? (
                  <span className="inspector-add-report-hint inspector-add-report-hint--warn">
                    No commercial project matches that work order.
                  </span>
                ) : null}
              </label>
            )}
          </section>

          <section
            className="inspector-add-report-section"
            aria-labelledby="inspector-add-report-contractor-heading"
          >
            <h2 id="inspector-add-report-contractor-heading" className="inspector-add-report-section-title">
              Contractor
            </h2>
            {!selectedProjectRow ? (
              <p className="project-detail-muted">Select a project to see contractor options.</p>
            ) : (
              <label className="phase-schedule-field">
                <span className="phase-schedule-field-label">
                  {projectContractorOptions.length > 1 ? "Which contractor?" : "Contractor"}
                </span>
                <input
                  className="commercial-field-input"
                  type="text"
                  list="inspector-add-report-contractors"
                  value={contractor}
                  disabled={busy || loadingProjects || loadingContractors}
                  placeholder={
                    contractorOptions.length
                      ? "Select or type contractor name (optional)"
                      : "Enter contractor name (optional)"
                  }
                  onChange={(e) => {
                    setContractor(e.target.value);
                    setError("");
                    setSuccess("");
                  }}
                />
                <datalist id="inspector-add-report-contractors">
                  {contractorOptions.map((name) => (
                    <option key={name} value={name} />
                  ))}
                </datalist>
                {projectContractorOptions.length > 1 ? (
                  <span className="inspector-add-report-hint">
                    This project has multiple contractors — choose the one that did today&apos;s work
                    if known.
                  </span>
                ) : null}
              </label>
            )}
          </section>

          <section className="inspector-add-report-section" aria-labelledby="inspector-add-report-progress-heading">
            <h2 id="inspector-add-report-progress-heading" className="inspector-add-report-section-title">
              Today&apos;s progress
            </h2>
            <ExpansionDailyProgressForm
              idPrefix="inspector-add-report"
              conduitFootage={conduitFootage}
              fiberFootage={fiberFootage}
              proofingFootage={proofingFootage}
              vaults={vaults}
              notes={notes}
              disabled={busy}
              onConduitFootageChange={setConduitFootage}
              onFiberFootageChange={setFiberFootage}
              onProofingFootageChange={setProofingFootage}
              onVaultsChange={setVaults}
              onNotesChange={setNotes}
            />
          </section>

          {error ? (
            <p className="project-detail-error" role="alert">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="inspector-add-report-success" role="status">
              {success}
            </p>
          ) : null}

          <div className="inspector-add-report-actions">
            <button
              type="submit"
              className="email-triggers-button email-triggers-button-primary"
              disabled={
                busy ||
                loadingProjects ||
                (isCommercial ? !selectedCommercialRow : !selectedExpansionRow)
              }
            >
              {busy ? "Saving…" : "Submit report"}
            </button>
            <Link to="/" className="project-detail-muted">
              Back to board
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export function InspectorAddReportGate(props) {
  return <InspectorAddReportPage {...props} />;
}
